import React, { useEffect, useState, useMemo, memo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Card from "./Cards/Card";
import allStaff from "../../../assets/dashboard/staff.png";
import Participant from "../../../assets/dashboard/participant.png";
import Complaints from "../../../assets/dashboard/complaint.png";
import Incident from "../../../assets/dashboard/incident.png";
import Feedback from "../../../assets/dashboard/feedback.png";
import ProgressTable from "./ProgressTable";
import ExpirtingDateTable from "./ExpiringDateTable";
import Tabs from "./Tabs";
import BirthdayCard from "./Cards/BirthdayCard";
import { connect } from "react-redux";
import {
  getSPDashboardRequest,
  spRosteringSettingsRequest,
} from "../../../store/actions/sp.actions";
import {
  getDashboard,
  getLoading,
  getSpRosteringSetting,
} from "../../../store/selector/sp.selector";
import { getUser } from "../../../store/selector/auth.selector";
import AnniversaryCard from "./Cards/AnniversaryCard";
import AnnualPerformanceCard from "./Cards/AnnualPerformanceCard";
import { apiGetRequest, apiPostRequest } from "helpers/Requests";
import {
  URL_HRM_GET_TEAM_MANAGMENT,
  URL_SP_PARTICIPANTS,
} from "store/services/URL";
import { roleAccess, canViewAuditLogActivity } from "helpers/RolesPermission";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import { getCurrentTimezone, parseBooleanVal, selectedTabState, capitalizeFirstLetter } from "Utils/utils";
import { isDashboardShiftVisible } from "./ExpiringDateTable/dashboardShiftUtils";
import { USER_ROLE, getParticipantLabel } from "Constants/constant";
import useCareSector from "hooks/useCareSector";
import api from "store/services/sp.services";
import AppointmentCard from "./Cards/UpcomingAppointments";
import { DashboardActivityWidget } from "components/common/AuditLog";

// Simple skeleton loader component for widget sections (memoized)
const WidgetSkeleton = memo(({ count = 3 }) => {
  return (
    <div className='flex flex-col space-y-2 sm:space-y-3'>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className='animate-pulse bg-gray-100 rounded-lg p-3 space-y-2'
        >
          <div className='flex items-center gap-3'>
            <div className='h-10 w-10 bg-gray-300 rounded-full'></div>
            <div className='flex-1 space-y-2'>
              <div className='h-4 bg-gray-300 rounded w-3/4'></div>
              <div className='h-3 bg-gray-300 rounded w-1/2'></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

WidgetSkeleton.displayName = 'WidgetSkeleton';

// Skeleton loader for table widgets (memoized)
const TableSkeleton = memo(() => {
  return (
    <div className='animate-pulse space-y-3'>
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className='flex items-center justify-between p-3 bg-gray-100 rounded'>
          <div className='flex items-center gap-3 flex-1'>
            <div className='h-10 w-10 bg-gray-300 rounded-full'></div>
            <div className='flex-1 space-y-2'>
              <div className='h-4 bg-gray-300 rounded w-2/3'></div>
              <div className='h-3 bg-gray-300 rounded w-1/3'></div>
            </div>
          </div>
          <div className='h-6 w-16 bg-gray-300 rounded'></div>
        </div>
      ))}
    </div>
  );
});

TableSkeleton.displayName = 'TableSkeleton';

const Dashboard = (props) => {
  const {
    loading,
    spDashboard,
    getDashboard,
    currentUser,
    spRosteringSetting,
    getSpRosteringSettingCall,
  } = props;

  const { careSector, isCareSectorPending } = useCareSector();
  const isAgedCare = careSector === "aged_care";

  const [birthdayList, setBirthdayList] = useState([]);
  const [workAnniversaries, setAnniversaries] = useState([]);
  const [upcomingAppointment, setUpAppointments] = useState([]);
  const [annualPerformances, setAnnualPerformances] = useState([]);
  const [expirDocs, setExpirDoc] = useState([]);
  const [expirDocsPage, setExpirDocsPage] = useState(1);
  const [expirDocsHasMore, setExpirDocsHasMore] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [shiftsPage, setShiftsPage] = useState(1);
  const [shiftsHasMore, setShiftsHasMore] = useState(false);
  const [participantsCompliance, setParticipantsCompliance] = useState([]);
  const [participantsCompliancePage, setParticipantsCompliancePage] = useState(1);
  const [participantsComplianceHasMore, setParticipantsComplianceHasMore] =
    useState(false);
  const [hrmCompliance, setHrmCompliance] = useState([]);
  const [hrmCompliancePage, setHrmCompliancePage] = useState(1);
  const [hrmComplianceHasMore, setHrmComplianceHasMore] = useState(false);

  const DASHBOARD_LIST_PER_PAGE = 10;

  // Individual loading states for each widget
  const [birthdayLoading, setBirthdayLoading] = useState(false);
  const [anniversaryLoading, setAnniversaryLoading] = useState(false);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [expirDocsLoading, setExpirDocsLoading] = useState(false);
  const [expirDocsLoadingMore, setExpirDocsLoadingMore] = useState(false);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [shiftsLoadingMore, setShiftsLoadingMore] = useState(false);
  const [participantsComplianceLoading, setParticipantsComplianceLoading] =
    useState(false);
  const [participantsComplianceLoadingMore, setParticipantsComplianceLoadingMore] =
    useState(false);
  const [hrmComplianceLoading, setHrmComplianceLoading] = useState(false);
  const [hrmComplianceLoadingMore, setHrmComplianceLoadingMore] =
    useState(false);

  const navigate = useNavigate();

  // Parallelize all independent API calls for faster loading
  useEffect(() => {
    // Execute all widget data fetches in parallel
    Promise.allSettled([
      getBirthdays(),
      getAnniversaries(),
      getExpirDocs(),
      getUpcomingShifts(),
      getParticipantsCompliance(),
      getHrmCompliance(),
      getAnnualPerformances(),
      getUpcomingAppointments(),
    ]);
  }, []);

  // Parallelize Redux actions for main dashboard data
  useEffect(() => {
    getDashboard({});
    getSpRosteringSettingCall(); // Fetch incident form settings for card redirect
  }, []);

  //get birthdays
  const getBirthdays = async (page) => {
    setBirthdayLoading(true);
    try {
      const res = await apiGetRequest(
        `/hrm/employee-upcoming-birthday?birthday_days=15&per_page=100`
      );
      if (res && res.status === 200) {
        const birthdays = res.data?.data?.birthdays;

        const birthdayArray = [];

        for (const key in birthdays) {
          if (birthdays.hasOwnProperty(key)) {
            const birthdayData = birthdays[key];
            birthdayArray.push(birthdayData);
          }
        }
        setBirthdayList(birthdayArray);
      }
    } catch (Err) {
      console.log(Err);
    } finally {
      setBirthdayLoading(false);
    }
  };

  //get anniversaries
  const getAnniversaries = async (page) => {
    setAnniversaryLoading(true);
    try {
      const res = await apiGetRequest(
        `/hrm/employee-work-anniversaries?anniversary_days=15&per_page=100`
      );
      if (res && res.status === 200) {
        setAnniversaries(res.data?.data?.anniversaries?.data);
      }
    } catch (Err) {
      console.log(Err);
    } finally {
      setAnniversaryLoading(false);
    }
  };
  //get annual performances
  const getAnnualPerformances = async (page) => {
    setPerformanceLoading(true);
    try {
      const res = await apiGetRequest(`/hrm/employee-performance`);

      if (res && res.status === 200) {
        setAnnualPerformances(res.data?.data);
      }
    } catch (Err) {
      console.log(Err);
    } finally {
      setPerformanceLoading(false);
    }
  };

  //get upcomig appointments
  const getUpcomingAppointments = async (page) => {
    setAppointmentLoading(true);
    try {
      const res = await apiGetRequest("/sp/participant/upcoming-appointment");
      if (res && res.status === 200) {
        setUpAppointments(res.data?.data || []);
      }
    } catch (Err) {
      console.log(Err);
    } finally {
      setAppointmentLoading(false);
    }
  };
  //get expire docs
  const getExpirDocs = async (page = 1, append = false) => {
    if (page === 1) {
      setExpirDocsLoading(true);
    } else {
      setExpirDocsLoadingMore(true);
    }
    try {
      const res = await apiGetRequest(
        `/hrm/expiring-docs?expiring_days=30&per_page=${DASHBOARD_LIST_PER_PAGE}&page=${page}`
      );
      if (res && res.status === 200) {
        const documents = res.data?.data?.documents?.data || [];
        const currentPage = res.data?.data?.documents?.current_page || page;
        const totalPages = res.data?.data?.documents?.total_pages || 1;
        setExpirDoc((prev) => (append ? [...prev, ...documents] : documents));
        setExpirDocsPage(currentPage);
        setExpirDocsHasMore(currentPage < totalPages);
      }
    } catch (Err) {
      console.log(Err);
    } finally {
      setExpirDocsLoading(false);
      setExpirDocsLoadingMore(false);
    }
  };

  const loadMoreExpirDocs = useCallback(() => {
    if (!expirDocsLoadingMore && expirDocsHasMore) {
      getExpirDocs(expirDocsPage + 1, true);
    }
  }, [expirDocsLoadingMore, expirDocsHasMore, expirDocsPage]);

  const getParticipantsCompliance = async (page = 1, append = false) => {
    if (page === 1) {
      setParticipantsComplianceLoading(true);
    } else {
      setParticipantsComplianceLoadingMore(true);
    }
    try {
      const res = await apiPostRequest(
        `${URL_SP_PARTICIPANTS}?page=${page}&per_page=${DASHBOARD_LIST_PER_PAGE}&compliance=true`,
        { filter: { status: ["active"] } }
      );
      if (res?.status === 200) {
        const participants = res.data?.data?.participant?.data || [];
        const currentPage = res.data?.data?.participant?.current_page || page;
        const totalPages = res.data?.data?.participant?.total_pages || 1;
        setParticipantsCompliance((prev) =>
          append ? [...prev, ...participants] : participants
        );
        setParticipantsCompliancePage(currentPage);
        setParticipantsComplianceHasMore(currentPage < totalPages);
      }
    } catch (Err) {
      console.log(Err);
    } finally {
      setParticipantsComplianceLoading(false);
      setParticipantsComplianceLoadingMore(false);
    }
  };

  const getHrmCompliance = async (page = 1, append = false) => {
    if (page === 1) {
      setHrmComplianceLoading(true);
    } else {
      setHrmComplianceLoadingMore(true);
    }
    try {
      const res = await apiPostRequest(
        `${URL_HRM_GET_TEAM_MANAGMENT}?page=${page}&per_page=${DASHBOARD_LIST_PER_PAGE}&compliance=true`,
        {}
      );
      if (res?.status === 200) {
        const employees = res.data?.data?.employees?.data || [];
        const currentPage = res.data?.data?.employees?.current_page || page;
        const totalPages = res.data?.data?.employees?.total_pages || 1;
        setHrmCompliance((prev) =>
          append ? [...prev, ...employees] : employees
        );
        setHrmCompliancePage(currentPage);
        setHrmComplianceHasMore(currentPage < totalPages);
      }
    } catch (Err) {
      console.log(Err);
    } finally {
      setHrmComplianceLoading(false);
      setHrmComplianceLoadingMore(false);
    }
  };

  const loadMoreParticipantsCompliance = useCallback(() => {
    if (!participantsComplianceLoadingMore && participantsComplianceHasMore) {
      getParticipantsCompliance(participantsCompliancePage + 1, true);
    }
  }, [
    participantsComplianceLoadingMore,
    participantsComplianceHasMore,
    participantsCompliancePage,
  ]);

  const loadMoreHrmCompliance = useCallback(() => {
    if (!hrmComplianceLoadingMore && hrmComplianceHasMore) {
      getHrmCompliance(hrmCompliancePage + 1, true);
    }
  }, [hrmComplianceLoadingMore, hrmComplianceHasMore, hrmCompliancePage]);

  //get upcoming shifts
  const getUpcomingShifts = async (page = 1, append = false) => {
    if (page === 1) {
      setShiftsLoading(true);
    } else {
      setShiftsLoadingMore(true);
    }
    try {
      const timezone = getCurrentTimezone();
      const res = await api.getSpDashboardInprogressShifts({
        timezone,
        page,
        per_page: DASHBOARD_LIST_PER_PAGE,
      });
      const shiftsData = (res?.shifts?.data || []).filter((shift) =>
        isDashboardShiftVisible(shift)
      );
      const currentPage = res?.shifts?.current_page || page;
      const totalPages = res?.shifts?.total_pages || 1;
      setShifts((prev) => (append ? [...prev, ...shiftsData] : shiftsData));
      setShiftsPage(currentPage);
      setShiftsHasMore(currentPage < totalPages);
    } catch (Err) {
      console.log(Err);
    } finally {
      setShiftsLoading(false);
      setShiftsLoadingMore(false);
    }
  };

  const loadMoreShifts = useCallback(() => {
    if (!shiftsLoadingMore && shiftsHasMore) {
      getUpcomingShifts(shiftsPage + 1, true);
    }
  }, [shiftsLoadingMore, shiftsHasMore, shiftsPage]);
  // Memoize card data to prevent unnecessary re-renders
  const activeParticipantsLabel = useMemo(
    () => `Active ${getParticipantLabel(careSector, true)}`,
    [careSector]
  );
  const participantsComplianceLabel = useMemo(
    () => `${getParticipantLabel(careSector, true)} Compliance`,
    [careSector]
  );
  const participantNameLabel = useMemo(
    () => `${getParticipantLabel(careSector, false)} Name`,
    [careSector]
  );

  const CardData = useMemo(() => [
    {
      id: "active-staff",
      title: "Active Staff ",
      value: spDashboard?.all_staff || 0,
      icon: allStaff,
      text: "",
    },
    {
      id: "active-participants",
      title: activeParticipantsLabel,
      titleLoading: isCareSectorPending,
      value: spDashboard?.all_participant_count || 0,
      icon: Participant,
      text: "",
    },
    {
      id: "feedback",
      title: "Feedback",
      value: spDashboard?.current_week_feedbacks || 0,
      icon: Feedback,
      text: "this week",
    },
    {
      id: "incidents",
      title: "Incidents",
      value: spDashboard?.incidents_count || 0,
      icon: Incident,
      text: "this week",
      bgColor: "#FF456E",
    },
  ], [spDashboard, activeParticipantsLabel, isCareSectorPending]);

  // Memoize allowed participant types to prevent recalculation
  const allowedParticipantTypes = useMemo(() => {
    const hasCorePermission = roleAccess(
      [],
      PERMISSION_KEYS.PARTICIPANT,
      PERMISSION_KEYS_CHILDREN.PARTICIPANT.CORE
    );
    const hasCosPermission = roleAccess(
      [],
      PERMISSION_KEYS.PARTICIPANT,
      PERMISSION_KEYS_CHILDREN.PARTICIPANT.COS
    );
    if (hasCorePermission && hasCosPermission) return ["Core", "COS", "Allied Health", "Aged Care"];
    if (hasCorePermission) return ["Core", "Allied Health", "Aged Care"];
    if (hasCosPermission) return ["COS", "Allied Health", "Aged Care"];
    return ["Allied Health", "Aged Care"];
  }, []); // Empty deps since roleAccess should be stable

  const filteredParticipantsCompliance = useMemo(
    () =>
      participantsCompliance.filter(
        (x) =>
          x?.status === "active" &&
          (Array.isArray(x.participant_type)
            ? x.participant_type.some((type) =>
                allowedParticipantTypes.includes(type?.name)
              )
            : allowedParticipantTypes.includes(x.participant_type))
      ),
    [participantsCompliance, allowedParticipantTypes]
  );

  const filteredHrmCompliance = useMemo(
    () => hrmCompliance.filter((x) => parseBooleanVal(x?.status)),
    [hrmCompliance]
  );

  // Memoize personalized greeting to prevent recalculation on every render
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    let timeGreeting = "Welcome back";

    if (hour < 12) {
      timeGreeting = "Good morning";
    } else if (hour < 17) {
      timeGreeting = "Good afternoon";
    } else {
      timeGreeting = "Good evening";
    }

    // Try multiple ways to get the user's name
    let fullName = "";

    if (currentUser) {
      // Try first_name + last_name
      const firstName = currentUser?.first_name || "";
      const lastName = currentUser?.last_name || "";
      fullName = `${firstName} ${lastName}`.trim();

      // If that doesn't work, try the name property
      if (!fullName && currentUser?.name) {
        fullName = currentUser.name.trim();
      }

      // Try service_provider name (for SP Admins)
      if (!fullName && currentUser?.service_provider?.name) {
        fullName = currentUser.service_provider.name.trim();
      }
    }

    if (fullName) {
      return `${timeGreeting}, ${fullName}!`;
    }
    return `${timeGreeting}!`;
  }, [currentUser]);

  return (
    <div className='min-h-full w-full bg-gradient-to-br from-gray-50 via-gray-50 to-blue-50'>
      <div className='w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6'>
        {/* Header Section */}
        <div className='mb-4 sm:mb-6'>
          <h1 className='text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2'>
            {greeting}
          </h1>
          <p className='text-sm sm:text-base text-gray-600'>
            Here's what's happening today.
          </p>
        </div>

        {/* Stats Cards Grid */}
        <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6'>
          {CardData?.map((card, index) => {
            const { id, title, value, icon, text, bgColor, titleLoading } = card;

            const handleClick = () => {
              if (id === "active-staff") {
                navigate("/hrm/team-management");
              } else if (id === "active-participants") {
                navigate("/participant-list");
              } else if (id === "feedback") {
                navigate("/feedbacks");
              } else if (id === "incidents") {
                const incidentFormId = spRosteringSetting?.incident_form_id;
                const isNewIncidentSystem = incidentFormId === -2 || incidentFormId === "-2";

                if (isNewIncidentSystem) {
                    const today = new Date();
                    const dayOfWeek = today.getDay();
                    const startOfWeek = new Date(today);
                    startOfWeek.setDate(today.getDate() - dayOfWeek);
                    startOfWeek.setHours(0, 0, 0, 0);
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 6);
                    endOfWeek.setHours(23, 59, 59, 999);

                    const fromDate = startOfWeek.toISOString().split("T")[0];
                    const toDate = endOfWeek.toISOString().split("T")[0];

                  navigate(`/reporting/incident-management-reports?fromDate=${fromDate}&toDate=${toDate}`,);
                } else {
                  navigate("/reporting/incident-reports");
                }
              }
            };

            return (
              <Card
                key={id || `${title}-${index}`}
                title={title}
                titleLoading={titleLoading}
                value={value}
                icon={icon}
                text={text}
                bgColor={bgColor}
                onClick={handleClick}
                isAgedCare={isAgedCare}
              />
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className='grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6'>
          {/* Left Section - Compliance Tables & Shifts */}
          <div className='lg:col-span-8 space-y-4 sm:space-y-6'>
            {/* Compliance Tables - Side by Side */}
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6'>
              {/* Participants Compliance - always show section (heading + content or empty state) */}
              {roleAccess([], PERMISSION_KEYS.PARTICIPANT) && (
                <div className='bg-white rounded-xl shadow-md hover:shadow-lg border border-gray-200 overflow-hidden transition-shadow'>
                  <div className='p-4 sm:p-6'>
                    {participantsComplianceLoading || isCareSectorPending ? (
                      <TableSkeleton />
                    ) : (
                      <ProgressTable
                        title={participantsComplianceLabel}
                        columnName={participantNameLabel}
                        item={filteredParticipantsCompliance}
                        onLoadMore={loadMoreParticipantsCompliance}
                        loadingMore={participantsComplianceLoadingMore}
                        hasMore={participantsComplianceHasMore}
                        isParticipantSection
                      />
                    )}
                  </div>
                </div>
              )}

              {/* HRM Compliance - always show section (heading + content or empty state) */}
              {roleAccess([], PERMISSION_KEYS.HRM) && (
                <div className='bg-white rounded-xl shadow-md hover:shadow-lg border border-gray-200 overflow-hidden transition-shadow'>
                  <div className='p-4 sm:p-6'>
                    {hrmComplianceLoading ? (
                      <TableSkeleton />
                    ) : (
                      <ProgressTable
                        title='HRM Compliance'
                        item={filteredHrmCompliance}
                        onLoadMore={loadMoreHrmCompliance}
                        loadingMore={hrmComplianceLoadingMore}
                        hasMore={hrmComplianceHasMore}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Scheduled Shifts & Expiring Docs - Side by Side */}
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6'>
              {/* Scheduled Shifts - always show section (heading + content or empty state) when user has permission */}
              {roleAccess([], PERMISSION_KEYS.ROSTERING) && (
                <div className='bg-white rounded-xl shadow-md hover:shadow-lg border border-gray-200 overflow-hidden transition-shadow'>
                  <div className='p-4 sm:p-6'>
                    {shiftsLoading ? (
                      <TableSkeleton />
                    ) : (
                      <ExpirtingDateTable
                        title='Scheduled Shift'
                        item={shifts || []}
                        onLoadMore={loadMoreShifts}
                        loadingMore={shiftsLoadingMore}
                        hasMore={shiftsHasMore}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Expiring Docs - always show section (heading + content or empty state) */}
              {roleAccess([], PERMISSION_KEYS.HRM) && (
                <div className='bg-white rounded-xl shadow-md hover:shadow-lg border border-gray-200 overflow-hidden transition-shadow'>
                  <div className='p-4 sm:p-6'>
                    {expirDocsLoading ? (
                      <TableSkeleton />
                    ) : (
                      <ExpirtingDateTable
                        title='Expiring Docs'
                        item={
                          expirDocs?.filter((x) => x.worker?.status === true) || []
                        }
                        isExpirDoc={true}
                        onLoadMore={loadMoreExpirDocs}
                        loadingMore={expirDocsLoadingMore}
                        hasMore={expirDocsHasMore}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className='lg:col-span-4 space-y-4 sm:space-y-6'>
            {/* Feeds Tabs */}
            <div className='bg-white rounded-xl shadow-md hover:shadow-lg border border-gray-200 overflow-hidden transition-shadow'>
              <div className='p-4 sm:p-6'>
                <Tabs />
              </div>
            </div>

            {/* Events & Appointments Section */}
            <div className='bg-white rounded-xl shadow-md hover:shadow-lg border border-gray-200 overflow-hidden transition-shadow'>
              <div className='p-4 sm:p-6'>
                {/* Birthday Events */}
                <div className='mb-4 sm:mb-6'>
                  <div className='flex items-center gap-2 mb-3 sm:mb-4'>
                    <div className='w-1 h-5 sm:h-6 bg-gradient-to-b from-pink-500 to-rose-500 rounded-full'></div>
                    <h3 className='text-base sm:text-lg font-bold text-gray-900'>Birthday Events</h3>
                  </div>
                  <div
                    className='flex flex-col overflow-y-auto custom-scroll space-y-2 sm:space-y-3'
                    style={{ maxHeight: "180px" }}
                  >
                    {birthdayLoading ? (
                      <WidgetSkeleton count={3} />
                    ) : birthdayList && birthdayList?.length > 0 ? (
                      birthdayList?.map((item) => (
                        <div key={item.id}>
                          <BirthdayCard item={item} />
                        </div>
                      ))
                    ) : (
                      <div className='text-center py-4 text-sm text-gray-500'>No data available</div>
                    )}
                  </div>
                </div>

                {/* Work Anniversary */}
                <div className='mb-4 sm:mb-6'>
                  <div className='flex items-center gap-2 mb-3 sm:mb-4'>
                    <div className='w-1 h-5 sm:h-6 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full'></div>
                    <h3 className='text-base sm:text-lg font-bold text-gray-900'>Work Anniversary</h3>
                  </div>
                  <div
                    className='flex flex-col overflow-y-auto custom-scroll space-y-2 sm:space-y-3'
                    style={{ maxHeight: "180px" }}
                  >
                    {anniversaryLoading ? (
                      <WidgetSkeleton count={3} />
                    ) : workAnniversaries && workAnniversaries?.length > 0 ? (
                      workAnniversaries?.map((item) => (
                        <div key={item?.id}>
                          <AnniversaryCard item={item} />
                        </div>
                      ))
                    ) : (
                      <div className='text-center py-4 text-sm text-gray-500'>No data available</div>
                    )}
                  </div>
                </div>

                {/* Planner/Task Calendar */}
                {roleAccess(
                  [],
                  PERMISSION_KEYS.PARTICIPANT,
                  PERMISSION_KEYS_CHILDREN.PARTICIPANT.COS
                ) && (
                  <div className='mb-4 sm:mb-6 min-w-0 overflow-hidden'>
                    <div className='flex items-center gap-2 mb-3 sm:mb-4'>
                      <div className='w-1 h-5 sm:h-6 bg-gradient-to-b from-green-500 to-teal-500 rounded-full'></div>
                      <h3 className='text-base sm:text-lg font-bold text-gray-900'>Upcoming Tasks</h3>
                      <span className='text-xs sm:text-sm text-gray-500'>(Next 7 Days)</span>
                    </div>
                    <div
                      className='flex flex-col overflow-y-auto overflow-x-hidden custom-scroll space-y-2 sm:space-y-3 min-h-0 w-full'
                      style={{ maxHeight: "500px" }}
                    >
                      {spDashboard?.upcoming_tasks && spDashboard?.upcoming_tasks?.length > 0 ? (
                        spDashboard?.upcoming_tasks?.map((task) => {
                          const dueDate = new Date(task.due_date);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const taskDueDate = new Date(dueDate);
                          taskDueDate.setHours(0, 0, 0, 0);
                          const daysUntilDue = Math.ceil((taskDueDate - today) / (1000 * 60 * 60 * 24));
                          const isOverdue = daysUntilDue < 0;
                          const isDueToday = daysUntilDue === 0;
                          const priorityColor = task.cos_priority?.color || '#6B7280';
                          const statusClass = {
                            'pending': 'bg-yellow-100 text-yellow-800',
                            'in_progress': 'bg-blue-100 text-blue-800',
                            'in-progress': 'bg-blue-100 text-blue-800'
                          }[task.status] || 'bg-gray-100 text-gray-800';

                          return (
                            <div
                              key={task.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                // If task has participant_uuid, navigate to participant profile with Tasks tab
                                if (task.participant_uuid) {
                                  const tasksTab = { id: 23, label: "Tasks", name: "tasks" };
                                  selectedTabState.save(JSON.stringify(tasksTab));
                                  navigate(`/sp-profile/${task.participant_uuid}`, {
                                    state: { selectedTab: tasksTab }
                                  });
                                } else {
                                  // User task, navigate to task planner
                                  navigate(`/my-task-planner?task=${task.uuid}`);
                                }
                              }}
                              className='p-3 sm:p-4 bg-gradient-to-r from-white to-gray-50 rounded-lg hover:shadow-md transition-all cursor-pointer border border-gray-200 hover:border-green-300 overflow-hidden flex-shrink-0 w-full max-w-full'
                              style={{ borderLeftWidth: '4px', borderLeftColor: priorityColor }}
                            >
                              <div className='flex items-start justify-between mb-2 gap-2 min-w-0'>
                                <div className='flex-1 min-w-0 overflow-hidden' style={{ minWidth: 0 }}>
                                  <p
                                    className='text-sm sm:text-base font-semibold text-gray-900'
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={task.task_name}
                                  >
                                    {task.task_name}
                                  </p>
                                  <p
                                    className='text-xs text-gray-600 mt-1'
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={task.participant_name || 'Personal Task'}
                                  >
                                    {task.participant_name
                                      ? task.participant_name.split(/\s+/).map((w) => (w.charAt(0).toUpperCase() + (w.slice(1) || '').toLowerCase())).join(' ')
                                      : 'Personal Task'}
                                  </p>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 ${statusClass}`}>
                                  {task.status?.replace('_', ' ')}
                                </span>
                              </div>
                              <div className='flex items-center justify-between text-xs sm:text-sm'>
                                <div className='flex items-center gap-2'>
                                  {task.cos_priority?.name && (
                                    <span
                                      className='px-2 py-0.5 rounded text-white font-medium text-xs'
                                      style={{ backgroundColor: priorityColor }}
                                    >
                                      {task.cos_priority.name}
                                    </span>
                                  )}
                                </div>
                                <span
                                  className={`font-medium ${
                                    isOverdue ? 'text-red-600' :
                                    isDueToday ? 'text-orange-600' :
                                    'text-gray-600'
                                  }`}
                                >
                                  {isOverdue ? 'Overdue' : isDueToday ? 'Due Today' : dueDate.toLocaleDateString('en-AU')}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className='text-center py-4 text-sm text-gray-500'>No upcoming tasks in the next 7 days</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Expiring Support Plans */}
                {roleAccess(
                  [],
                  PERMISSION_KEYS.PARTICIPANT,
                  PERMISSION_KEYS_CHILDREN.PARTICIPANT.SHOW
                ) && (
                  <div className='mb-4 sm:mb-6'>
                    <div className='flex items-center gap-2 mb-3 sm:mb-4'>
                      <div className='w-1 h-5 sm:h-6 bg-gradient-to-b from-orange-500 to-red-500 rounded-full'></div>
                      <h3 className='text-base sm:text-lg font-bold text-gray-900'>Expiring Support Plans</h3>
                      <span className='text-xs sm:text-sm text-gray-500'>(Next 30 Days)</span>
                    </div>
                    <div
                      className='flex flex-col overflow-y-auto custom-scroll space-y-2 sm:space-y-3'
                      style={{ maxHeight: "180px" }}
                    >
                      {spDashboard?.expiring_support_plans && spDashboard?.expiring_support_plans?.length > 0 ? (
                        spDashboard?.expiring_support_plans?.map((participant) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const endDate = new Date(participant.plan_end_date);
                          endDate.setHours(0, 0, 0, 0);
                          const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
                          const isExpired = daysLeft < 0;
                          const isExpiringToday = daysLeft === 0;

                          // Color coding: expired = red, today = red, <=7 days = red, <=14 days = orange, else yellow
                          const urgencyColor = isExpired || isExpiringToday || daysLeft <= 7
                            ? 'text-red-600'
                            : daysLeft <= 14
                            ? 'text-orange-600'
                            : 'text-yellow-600';

                          return (
                            <div
                              key={participant.id}
                              className={`flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg hover:shadow-md transition-all cursor-pointer border ${isExpired ? 'border-red-300 hover:border-red-400' : 'border-gray-200 hover:border-orange-300'}`}
                              onClick={() => {
                                const userType = localStorage.getItem('userType');
                                const participantRoute = userType === USER_ROLE.Sp_Standard_User
                                  ? `/participant-profile/${participant.uuid}`
                                  : `/sp-profile/${participant.uuid}`;

                                // Save Settings tab state (id: 13) to open settings tab
                                selectedTabState.save(JSON.stringify({
                                  name: 'Settings',
                                  id: 13,
                                  children: []
                                }));

                                navigate(participantRoute);
                              }}
                            >
                              <div className='flex-1'>
                                <p className='text-sm sm:text-base font-semibold text-gray-900'>
                                  {participant.first_name?.charAt(0).toUpperCase() + participant.first_name?.slice(1).toLowerCase()} {participant.last_name?.charAt(0).toUpperCase() + participant.last_name?.slice(1).toLowerCase()}
                                </p>
                                <p className='text-xs sm:text-sm text-gray-600 mt-1'>
                                  {isExpired ? 'Expired' : 'Expires'}: {new Date(participant.plan_end_date).toLocaleDateString('en-AU')}
                                </p>
                              </div>
                              <div className='text-right'>
                                <p className={`text-xs sm:text-sm font-bold ${urgencyColor}`}>
                                  {isExpired
                                    ? `${Math.abs(daysLeft)} ${Math.abs(daysLeft) === 1 ? 'day' : 'days'} ago`
                                    : isExpiringToday
                                    ? 'Today'
                                    : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`}
                                </p>
                                <p className='text-xs text-gray-500'>
                                  {isExpired ? 'expired' : isExpiringToday ? 'expires' : 'remaining'}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className='text-center py-4 text-sm text-gray-500'>No expiring support plans in the next 30 days</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Performance Reviews */}
                {roleAccess(
                  [],
                  PERMISSION_KEYS.HRM,
                  PERMISSION_KEYS_CHILDREN.HRM.SHOW
                ) && (
                  <div className='mb-4 sm:mb-6'>
                    <div className='flex items-center gap-2 mb-3 sm:mb-4'>
                      <div className='w-1 h-5 sm:h-6 bg-gradient-to-b from-purple-500 to-indigo-500 rounded-full'></div>
                      <h3 className='text-base sm:text-lg font-bold text-gray-900'>Performance Reviews</h3>
                    </div>
                    <div
                      className='flex flex-col overflow-y-auto custom-scroll space-y-2 sm:space-y-3'
                      style={{ maxHeight: "180px" }}
                    >
                      {performanceLoading ? (
                        <WidgetSkeleton count={3} />
                      ) : annualPerformances && annualPerformances?.length > 0 ? (
                        annualPerformances?.map((item) => (
                          <div key={item?.id}>
                            <AnnualPerformanceCard item={item} />
                          </div>
                        ))
                      ) : (
                        <div className='text-center py-4 text-sm text-gray-500'>No data available</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Upcoming Appointments */}
                <div>
                  <div className='flex items-center gap-2 mb-3 sm:mb-4'>
                    <div className='w-1 h-5 sm:h-6 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full'></div>
                    <h3 className='text-base sm:text-lg font-bold text-gray-900'>Upcoming Appointments</h3>
                  </div>
                  <div
                    className='flex flex-col overflow-y-auto custom-scroll space-y-2 sm:space-y-3'
                    style={{ maxHeight: "180px" }}
                  >
                    {appointmentLoading ? (
                      <WidgetSkeleton count={3} />
                    ) : upcomingAppointment && upcomingAppointment?.length > 0 ? (
                      upcomingAppointment?.map((item) => (
                        <div key={item?.id}>
                          <AppointmentCard item={item} />
                        </div>
                      ))
                    ) : (
                      <div className='text-center py-4 text-sm text-gray-500'>No data available</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {canViewAuditLogActivity(spRosteringSetting) && (
          <div className='mt-4 sm:mt-6'>
            <DashboardActivityWidget />
          </div>
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  getDashboard: getSPDashboardRequest,
  getSpRosteringSettingCall: spRosteringSettingsRequest,
};

const mapStateToProps = (state) => {
  const spDashboard = getDashboard(state);
  const loading = getLoading(state);
  const currentUser = getUser(state);
  const spRosteringSetting = getSpRosteringSetting(state);

  return {
    loading,
    spDashboard,
    currentUser,
    spRosteringSetting,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
